/**
 * Lättviktig GTM/dataLayer-hjälpare.
 * Pushar händelser till window.dataLayer så de kan fångas upp som triggers i
 * Google Tag Manager. Allt är fail-safe – får aldrig krascha UI:t.
 */

import { trackFunnel } from "@/lib/analytics";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export type AuthMethod = "email" | "google";
export type AuthLocation = "auth_page" | "checkout" | "fullmakt";

export const pushDataLayer = (event: string, payload: Record<string, unknown> = {}) => {
  if (typeof window === "undefined") return;
  try {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({ event, ...payload });
  } catch {
    /* ignorera */
  }
};

/** Användaren startar ett inloggningsförsök. */
export const trackLoginStart = (method: AuthMethod, location: AuthLocation) => {
  pushDataLayer("login_start", { method, auth_location: location });
  trackFunnel({ goal: "inloggning", stage: "start", formKey: location, metadata: { method, location } });
};

/** Inloggning lyckades. */
export const trackLogin = (method: AuthMethod, location: AuthLocation) => {
  pushDataLayer("login", { method, auth_location: location });
  trackFunnel({ goal: "inloggning", stage: "complete", formKey: location, metadata: { method, location } });
};

/** Inloggning misslyckades. */
export const trackLoginFailed = (method: AuthMethod, location: AuthLocation, reason?: string) =>
  pushDataLayer("login_failed", { method, auth_location: location, reason: reason ?? null });

/** Användaren startar kontoskapande. */
export const trackSignUpStart = (method: AuthMethod, location: AuthLocation) => {
  pushDataLayer("sign_up_start", { method, auth_location: location });
  trackFunnel({ goal: "konto", stage: "start", formKey: location, metadata: { method, location } });
};

/** Konto skapat (kan kräva e-postverifiering). */
export const trackSignUp = (method: AuthMethod, location: AuthLocation) => {
  pushDataLayer("sign_up", { method, auth_location: location });
  trackFunnel({ goal: "konto", stage: "complete", formKey: location, metadata: { method, location } });
};

/** Kontoskapande misslyckades. */
export const trackSignUpFailed = (method: AuthMethod, location: AuthLocation, reason?: string) =>
  pushDataLayer("sign_up_failed", { method, auth_location: location, reason: reason ?? null });
